import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bath,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Droplets,
  FileText,
  Hammer,
  Home,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  PlugZap,
  SearchCheck,
  ShieldCheck,
  Sparkles,
  Star,
  Timer,
  Wrench,
  X,
} from "lucide-react";
import { toast } from "sonner";
import ContactSection from "../components/ContactSection";
import { Logo } from "../components/Logo";
import {
  waContactLink,
  waNumber,
  WhatsAppFab,
} from "../components/WhatsAppFab";
import { api, errMsg } from "../lib/api";
import { useBranding } from "../lib/branding";

const INSPECTION_AREAS = [
  [
    Home,
    "Flooring",
    "Tiles, alignment, hollow sounds, visible damage and finishing.",
  ],
  [
    SearchCheck,
    "Walls & Ceiling",
    "Visible cracks, patches, surface defects and finishing.",
  ],
  [
    Wrench,
    "Doors & Windows",
    "Alignment, operation, locks, handles, gaps and finishing.",
  ],
  [
    PlugZap,
    "Electrical",
    "Switches, sockets, fixtures and visible electrical points.",
  ],
  [
    Droplets,
    "Plumbing",
    "Visible fixtures, water flow and drainage observations.",
  ],
  [
    Bath,
    "Bathrooms",
    "Tiles, sanitary fittings, doors, ventilation and visible finishing.",
  ],
  [
    Building2,
    "Kitchen",
    "Countertop, cabinets, sink, plumbing and visible fittings.",
  ],
  [
    MapPin,
    "Balcony & Utility",
    "Drainage, flooring, doors, railings and visible finishing.",
  ],
  [
    Sparkles,
    "Overall Finishing",
    "Paint, sealant, hardware and visible workmanship issues.",
  ],
];

const REASONS = [
  [
    ShieldCheck,
    "Independent inspection",
    "We document visible observations independently.",
  ],
  [
    ClipboardCheck,
    "Detailed documentation",
    "Findings are recorded with photos and clear descriptions.",
  ],
  [
    FileText,
    "Clear report",
    "Understand what was observed and what may need attention.",
  ],
  [
    Timer,
    "Before handover",
    "Raise visible issues while you are still at the possession stage.",
  ],
];

const FINDINGS = [
  [
    "Door Alignment",
    "Main door shutter observed to be jamming during operation.",
    "Adjustment of door alignment and hardware is recommended.",
  ],
  [
    "Bathroom Finishing",
    "Incomplete paint finish observed on the bathroom door frame.",
    "Complete surface preparation and touch-up painting are recommended.",
  ],
  [
    "Window Sealing",
    "Gaps observed in silicone sealing around the window frame.",
    "Proper sealing of the affected areas is recommended.",
  ],
];

const FAQS = [
  [
    "What is a home inspection?",
    "A systematic visual and functional review of accessible property areas, recorded in a clear report.",
  ],
  [
    "Why inspect before possession?",
    "A focused inspection helps you identify visible defects and unfinished work before accepting handover.",
  ],
  [
    "Do you inspect apartments and villas?",
    "Yes. We inspect apartments, villas and independent houses, based on accessible areas and the selected service.",
  ],
  [
    "When should I book an inspection?",
    "Book before your final builder handover or as early as your property access allows.",
  ],
  [
    "Will I receive a report with photographs?",
    "Your report documents recorded findings, including photographs where captured during the inspection.",
  ],
  [
    "Can an inspection identify hidden defects?",
    "The inspection covers accessible, visible and practically testable areas. Concealed conditions are not guaranteed to be identified.",
  ],
];

const scrollToQuote = () =>
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    ["#inspect", "What we inspect", "public-nav-inspect-link"],
    ["#how-it-works", "How it works", "public-nav-process-link"],
    ["#sample-report", "Sample report", "public-nav-report-link"],
    ["#faqs", "FAQs", "public-nav-faq-link"],
    ["#contact", "Contact", "public-nav-contact-link"],
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
        <a href="#top" data-testid="public-logo-link">
          <Logo />
        </a>
        <nav className="hidden items-center gap-6 text-sm font-semibold text-slate-600 lg:flex">
          {links.map(([href, label, testid]) => (
            <a key={href} href={href} data-testid={testid}>
              {label}
            </a>
          ))}
        </nav>
        <button
          onClick={() => setMenuOpen((open) => !open)}
          className="tap grid h-10 w-10 place-items-center rounded-md border border-slate-200 lg:hidden"
          data-testid="public-mobile-menu-button"
          aria-label="Open navigation menu"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <button
          onClick={scrollToQuote}
          className="tap hidden h-10 whitespace-nowrap rounded-md bg-slate-900 px-4 text-sm font-bold text-white hover:bg-teal-700 sm:block"
          data-testid="public-header-book-button"
        >
          Book inspection
        </button>
      </div>
      {menuOpen && (
        <nav
          className="border-t border-slate-200 bg-white px-5 py-3 lg:hidden"
          data-testid="public-mobile-navigation"
        >
          <div className="grid gap-1">
            {links.map(([href, label, testid]) => (
              <a
                key={href}
                href={href}
                onClick={() => setMenuOpen(false)}
                className="tap rounded-md px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                data-testid={`mobile-${testid}`}
              >
                {label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function MobileActions() {
  const branding = useBranding() || {};
  const phone = (branding.phone || "").replace(/\D/g, "");
  const whatsapp = waNumber(branding);
  const hasWhatsapp = whatsapp.length >= 10;
  const callHref = phone.length >= 10 ? `tel:+${phone}` : "#contact";
  const whatsappHref = hasWhatsapp ? waContactLink(branding) : "#contact";

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-3 border-t border-slate-200 bg-white p-2 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] sm:hidden">
      <a
        href={callHref}
        className="tap flex h-11 items-center justify-center gap-1 text-xs font-bold text-slate-700"
        data-testid="mobile-call-button"
      >
        <Phone className="h-4 w-4" />
        Call
      </a>
      <a
        href={whatsappHref}
        target={hasWhatsapp ? "_blank" : undefined}
        rel="noreferrer"
        className="tap flex h-11 items-center justify-center gap-1 rounded-md bg-[#25D366] text-xs font-bold text-white"
        data-testid="mobile-whatsapp-button"
      >
        <MessageCircle className="h-4 w-4" />
        WhatsApp
      </a>
      <button
        onClick={scrollToQuote}
        className="tap h-11 rounded-md bg-slate-900 text-xs font-bold text-white"
        data-testid="mobile-book-button"
      >
        Book inspection
      </button>
    </div>
  );
}

export default function PublicMarketing() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Home Inspection Bangalore | PossessionCheck";
    const description =
      "Independent home, apartment and pre-possession inspections in Bangalore.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  const sampleReport = async () => {
    try {
      const { data } = await api.get("/public/sample-report");
      navigate(`/report/${data.inspection_id}?t=${data.token}&sample=1`);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  return (
    <div id="top" className="bg-[#F7FAF9] pb-16 text-slate-900 sm:pb-0">
      <Header />
      <main>
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <img
            src="/images/walkthrough.jpg"
            alt="Professional inspecting a modern apartment interior"
            className="absolute inset-0 h-full w-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,23,28,0.94),rgba(8,23,28,0.72),rgba(8,23,28,0.24))]" />
          <div className="relative mx-auto grid min-h-[600px] max-w-7xl items-end px-5 py-16 lg:min-h-[660px] lg:py-20">
            <div className="max-w-3xl fade-up">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-300/30 bg-teal-300/10 px-3 py-1.5 font-mono text-[10px] font-bold tracking-[0.18em] text-teal-100">
                <MapPin className="h-3.5 w-3.5" />
                BANGALORE PROPERTY INSPECTION
              </div>
              <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
                Before You Take the Keys,{" "}
                <span className="text-teal-300">Check the Home.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
                Independent property inspection to help identify visible
                defects, unfinished work and quality issues before you accept
                possession.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <button
                  onClick={scrollToQuote}
                  className="tap flex h-12 items-center gap-2 rounded-md bg-teal-400 px-6 font-bold text-slate-950 hover:bg-teal-300"
                  data-testid="hero-book-inspection-button"
                >
                  Book an inspection <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={scrollToQuote}
                  className="tap h-12 rounded-md border border-white/40 bg-white/10 px-6 font-bold text-white hover:bg-white/20"
                  data-testid="hero-free-quote-button"
                >
                  Get a free quote
                </button>
              </div>
              <div className="mt-12 flex flex-wrap gap-x-7 gap-y-3 text-sm text-slate-200">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-300" />
                  Apartments, villas & independent homes
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-300" />
                  Clear photo-supported report
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-14">
          <div className="mx-auto grid max-w-7xl gap-8 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <div className="overline mb-3">
                Why inspect before possession?
              </div>
              <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
                A new home can look complete at first glance.
              </h2>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-slate-600">
              A normal walkthrough can miss finishing gaps and visible issues. A
              systematic property inspection helps you understand what deserves
              attention before you accept handover from the builder.
            </p>
          </div>
        </section>

        <section id="inspect" className="scroll-mt-16 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-5">
            <div className="overline mb-3">What do we inspect?</div>
            <h2 className="max-w-2xl font-display text-3xl font-extrabold sm:text-4xl">
              A practical review of the home you are about to receive.
            </h2>
            <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {INSPECTION_AREAS.map(([Icon, title, text]) => (
                <article
                  key={title}
                  className="group border border-slate-200 bg-white p-5 transition-[border-color,transform] duration-200 hover:-translate-y-1 hover:border-teal-500"
                  data-testid={`inspection-area-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <Icon className="h-6 w-6 text-teal-700" />
                  <h3 className="mt-5 font-display text-xl font-bold">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {text}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#EAF5F2] py-20 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <div className="overline mb-3">Why PossessionCheck?</div>
              <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
                Professional process. Clear information. Better handover
                conversations.
              </h2>
              <p className="mt-5 max-w-xl text-slate-600">
                Our report is designed to help you understand recorded
                observations without confusing internal terminology or
                unnecessary jargon.
              </p>
              <button
                onClick={scrollToQuote}
                className="tap mt-8 h-12 rounded-md bg-slate-900 px-6 font-bold text-white hover:bg-teal-700"
                data-testid="why-book-button"
              >
                Talk to an expert
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {REASONS.map(([Icon, title, text]) => (
                <div
                  key={title}
                  className="border border-teal-900/10 bg-white p-5"
                >
                  <Icon className="h-6 w-6 text-teal-700" />
                  <h3 className="mt-5 font-display text-lg font-bold">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="scroll-mt-16 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-5">
            <div className="flex flex-wrap items-end justify-between gap-5">
              <div>
                <div className="overline mb-3">How it works</div>
                <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
                  Straightforward from booking to report.
                </h2>
              </div>
              <button
                onClick={scrollToQuote}
                className="tap h-11 rounded-md border border-slate-900 px-5 text-sm font-bold"
                data-testid="process-book-button"
              >
                Book your inspection
              </button>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-4">
              {[
                [
                  "01",
                  "Book your inspection",
                  "Choose your property and a preferred inspection date.",
                ],
                [
                  "02",
                  "We inspect the property",
                  "Our team checks accessible areas systematically.",
                ],
                [
                  "03",
                  "Findings are documented",
                  "Recorded observations include photographs where captured.",
                ],
                [
                  "04",
                  "Receive your report",
                  "Understand what needs attention before possession.",
                ],
              ].map(([number, title, text]) => (
                <div key={number} className="border-t-2 border-teal-500 pt-5">
                  <div className="font-mono text-xs font-bold text-teal-700">
                    {number}
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="sample-report"
          className="scroll-mt-16 bg-slate-950 py-20 text-white sm:py-24"
        >
          <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <div className="overline mb-3 text-teal-300">
                See what you receive
              </div>
              <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
                See What Your Inspection Report Looks Like
              </h2>
              <p className="mt-5 max-w-xl leading-relaxed text-slate-300">
                A structured report brings together your property summary,
                recorded findings, photos, severity and recommendations in one
                clear place.
              </p>
              <button
                onClick={sampleReport}
                className="tap mt-8 flex h-12 items-center gap-2 rounded-md bg-teal-400 px-6 font-bold text-slate-950 hover:bg-teal-300"
                data-testid="sample-report-button"
              >
                <FileText className="h-4 w-4" />
                View sample report
              </button>
            </div>
            <div
              className="relative mx-auto w-full max-w-md border border-slate-600 bg-white p-5 text-slate-900 shadow-2xl"
              data-testid="sample-report-preview"
            >
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                <div>
                  <div className="font-mono text-[10px] font-bold tracking-widest text-teal-700">
                    HOME INSPECTION REPORT
                  </div>
                  <div className="mt-1 font-display text-xl font-bold">
                    Sample Residence
                  </div>
                </div>
                <div className="grid h-14 w-14 place-items-center rounded-full border-4 border-teal-500 font-display text-xl font-extrabold">
                  81
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                <div className="border border-slate-200 p-2">
                  <b className="block text-rose-600">3</b>
                  <span className="text-[10px] text-slate-500">Priority</span>
                </div>
                <div className="border border-slate-200 p-2">
                  <b className="block text-amber-600">6</b>
                  <span className="text-[10px] text-slate-500">Attention</span>
                </div>
                <div className="border border-slate-200 p-2">
                  <b className="block text-blue-600">8</b>
                  <span className="text-[10px] text-slate-500">Finishing</span>
                </div>
              </div>
              <div className="mt-5 overflow-hidden border border-slate-200">
                <img
                  src="/images/cracked_tile.jpg"
                  alt="Sample report photo of a visible tile defect"
                  className="aspect-[16/8] w-full object-cover"
                  loading="lazy"
                />
                <div className="p-3">
                  <div className="font-mono text-[10px] text-slate-500">
                    FINDING · BEDROOM 2
                  </div>
                  <div className="mt-1 font-bold">Visible tile crack</div>
                  <p className="mt-1 text-xs text-slate-600">
                    Recommendation provided with photo evidence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-5">
            <div className="overline mb-3">Sample inspection findings</div>
            <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
              Examples of the clear, practical language in your report.
            </h2>
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {FINDINGS.map(([title, observation, recommendation]) => (
                <article
                  key={title}
                  className="border border-slate-200 bg-white p-6"
                >
                  <div className="flex items-center gap-2 text-sm font-bold text-teal-700">
                    <Star className="h-4 w-4" />
                    {title}
                  </div>
                  <div className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400">
                    Observation
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    {observation}
                  </p>
                  <div className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400">
                    Recommendation
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    {recommendation}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white py-20">
          <div className="mx-auto max-w-7xl px-5">
            <div className="overline mb-3">Is this inspection for you?</div>
            <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
              For buyers who want a more informed handover.
            </h2>
            <div className="mt-10 grid gap-4 md:grid-cols-4">
              {[
                [
                  "Taking possession of a new apartment?",
                  "Inspect before accepting handover.",
                ],
                [
                  "Buying a resale property?",
                  "Understand visible issues before purchase.",
                ],
                [
                  "Buying a villa?",
                  "Get a systematic review of accessible areas.",
                ],
                [
                  "Buying property in Bangalore?",
                  "Get an independent inspection before your final decision.",
                ],
              ].map(([title, text]) => (
                <div key={title} className="border-l-2 border-teal-500 pl-4">
                  <h3 className="font-display text-lg font-bold">{title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-5">
            <div className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <div className="overline mb-3">
                  Property inspection in Bangalore
                </div>
                <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
                  A local inspection service for your next handover.
                </h2>
              </div>
              <div>
                <p className="leading-relaxed text-slate-600">
                  PossessionCheck supports homebuyers seeking apartment
                  inspection, villa inspection and pre-possession inspection in
                  Bangalore. Whether your property is in Sarjapur, Whitefield,
                  Electronic City, Bellandur or HSR Layout, we help you document
                  visible observations before handover.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {[
                    "Apartment inspection in Bangalore",
                    "Pre-possession inspection",
                    "New home inspection",
                    "Villa inspection",
                    "Sarjapur",
                    "Whitefield",
                    "Electronic City",
                    "Bellandur",
                    "HSR Layout",
                  ].map((label) => (
                    <a
                      key={label}
                      href="#contact"
                      className="tap rounded-full border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-teal-600"
                      data-testid={`seo-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="faqs" className="scroll-mt-16 bg-white py-20 sm:py-24">
          <div className="mx-auto max-w-4xl px-5">
            <div className="overline mb-3">FAQs</div>
            <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
              Questions before you book?
            </h2>
            <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
              {FAQS.map(([question, answer]) => (
                <details
                  key={question}
                  className="group py-5"
                  data-testid={`faq-${question.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <summary className="cursor-pointer list-none pr-8 font-display text-lg font-bold marker:hidden">
                    {question}
                    <span className="float-right text-teal-700 transition-transform group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
                    {answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <ContactSection />
      </main>
      <footer className="border-t border-slate-200 bg-slate-950 py-10 text-slate-300">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 md:grid-cols-[1fr_auto]">
          <div>
            <Logo dark />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
              Professional property inspection services for homebuyers preparing
              for possession and handover.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-3 text-sm">
            <a href="#top" data-testid="footer-home-link">
              Home
            </a>
            <a href="#inspect" data-testid="footer-services-link">
              Services
            </a>
            <a href="#sample-report" data-testid="footer-report-link">
              Sample report
            </a>
            <a href="#faqs" data-testid="footer-faq-link">
              FAQs
            </a>
            <a href="#contact" data-testid="footer-contact-link">
              Contact
            </a>
          </div>
        </div>
      </footer>
      <WhatsAppFab />
      <MobileActions />
    </div>
  );
}
